import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUpRight, Code, Smartphone, Server, Cloud, Users, Building2, ShoppingCart, Factory, CloudIcon, HeartPulse, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useMockData } from '../data/useMockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

// Lazy load Spline only when needed
const Spline = lazy(() => import('@splinetool/react-spline'));

const serviceIcons = {
  'web-development': Code,
  'mobile-development': Smartphone,
  'backend-apis': Server,
  'cloud-devops': Cloud,
  'consulting-architecture': Users,
};

const industryIcons = {
  'building': Building2,
  'shopping-cart': ShoppingCart,
  'factory': Factory,
  'cloud': CloudIcon,
  'heart-pulse': HeartPulse,
  'truck': Truck,
};

const HomePage = () => {
  const { t } = useTranslation();
  const { services, caseStudies, benefits, workProcess, industries, faqs, companyInfo } = useMockData();
  const [shouldLoadSpline, setShouldLoadSpline] = useState(false);

  // Load Spline only when hero section is in viewport (Intersection Observer)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const isFastConnection = !connection || connection.effectiveType === '4g';
      
      if (isFastConnection) {
        // Use Intersection Observer - load only when visible
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !shouldLoadSpline) {
                setShouldLoadSpline(true);
                observer.disconnect();
              }
            });
          },
          { rootMargin: '100px' } // Start loading 100px before visible
        );

        const heroSection = document.querySelector('section'); // First section (hero)
        if (heroSection) {
          observer.observe(heroSection);
        }

        return () => observer.disconnect();
      }
    }
  }, [shouldLoadSpline]);
  
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="grid-pattern absolute inset-0 opacity-50" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="stagger-children">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 mb-8">
                <span className="w-2 h-2 bg-[#00FFD1] rounded-full animate-pulse" />
                <span className="text-white/70 text-sm">{t('hero.badge')}</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
                {t('hero.title1')}<br />
                <span className="text-gradient">{t('hero.title2')}</span>
              </h1>
              
              <p className="text-xl text-white/70 max-w-xl mb-10 leading-relaxed">
                {t('hero.description')}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 text-lg font-medium h-auto">
                    {t('nav.bookCall')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="bg-white/10 text-white border-0 hover:bg-white hover:text-black px-8 py-4 text-lg font-medium h-auto"
                >
                  {t('hero.downloadDeck')}
                  <ArrowUpRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            
            {/* Right - 3D Element */}
            <div className="hidden lg:block relative">
              <div className="w-[700px] h-[700px] -mr-32 overflow-visible relative">
                {shouldLoadSpline ? (
                  <Suspense fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-16 h-16 border-2 border-[#00FFD1] border-t-transparent rounded-full animate-spin" />
                    </div>
                  }>
                    <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
                  </Suspense>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-16 h-16 border-2 border-[#00FFD1]/30 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Industries */}
      <section className="py-16 border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="text-white/70 text-sm text-center mb-8 uppercase tracking-wider">
            {t('industries.title')}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {industries.map((industry) => {
              const IconComponent = industryIcons[industry.icon];
              return (
                <div key={industry.name} className="flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300">
                  {IconComponent && <IconComponent className="w-5 h-5" />}
                  <span className="text-base font-medium">{industry.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Do - Services */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">{t('services.title')}</h2>
            <p className="text-lg text-white/60">
              {t('services.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 5).map((service) => {
              const IconComponent = serviceIcons[service.id];
              return (
                <Link
                  key={service.id}
                  to={`/services#${service.id}`}
                  className="group p-8 bg-white/[0.02] border border-white/10 hover:border-[#00FFD1]/50 hover:bg-white/[0.04] transition-all duration-400"
                >
                  <div className="w-12 h-12 bg-[#00FFD1]/10 flex items-center justify-center mb-6 group-hover:bg-[#00FFD1]/20 transition-colors duration-400">
                    {IconComponent && <IconComponent className="w-6 h-6 text-[#00FFD1]" />}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-white/60 text-base leading-relaxed mb-6">
                    {service.shortDescription}
                  </p>
                  <div className="flex items-center gap-2 text-[#00FFD1] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                    {t('services.learnMore')}
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Solid Consulting */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
                {t('whyUs.title')}
              </h2>
              <p className="text-lg text-white/60 mb-8">
                {t('whyUs.subtitle')}
              </p>
              <Link to="/about">
                <Button variant="outline" className="bg-transparent text-white border border-white/20 hover:bg-white hover:text-black px-6 py-3">
                  {t('whyUs.learnMore')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="p-6 border border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mb-4">{t('caseStudies.title')}</h2>
              <p className="text-lg text-white/60">{t('caseStudies.subtitle')}</p>
            </div>
            <Link to="/case-studies">
              <Button variant="outline" className="bg-transparent text-white border border-white/20 hover:bg-white hover:text-black">
                {t('caseStudies.viewAll')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {caseStudies.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/case-studies#${study.id}`}
                className="group p-8 bg-white/[0.02] border border-white/10 hover:border-[#00FFD1]/50 transition-all duration-400"
              >
                <span className="text-[#00FFD1] text-sm font-medium mb-4 block">{study.industry}</span>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#00FFD1] transition-colors duration-300">
                  {study.title}
                </h3>
                <p className="text-white/60 text-sm mb-6 line-clamp-2">{study.challenge}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {study.results.slice(0, 2).map((result, idx) => (
                    <span key={idx} className="px-3 py-1 bg-white/5 text-white/80 text-xs font-medium">
                      {result.value}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-[#00FFD1] text-sm font-medium">
                  {t('caseStudies.readCase')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">{t('howWeWork.title')}</h2>
            <p className="text-lg text-white/60">
              {t('howWeWork.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {workProcess.map((step, index) => (
              <div key={index} className="relative p-8 bg-black border border-white/10">
                <span className="text-6xl font-bold text-white/5 absolute top-4 right-6">
                  {String(step.step).padStart(2, '0')}
                </span>
                <div className="relative z-10">
                  <span className="inline-block w-10 h-10 bg-[#00FFD1] text-black text-lg font-semibold flex items-center justify-center mb-6">
                    {step.step}
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
                {t('faq.title')}
              </h2>
              <p className="text-lg text-white/60 mb-8">
                {t('faq.subtitle')}
              </p>
              <Link to="/contact">
                <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-6 py-3">
                  {t('faq.contactUs')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="border border-white/10 px-6 data-[state=open]:border-[#00FFD1]/30 transition-colors duration-300"
                  >
                    <AccordionTrigger className="text-white text-left text-base font-medium py-5 hover:no-underline hover:text-[#00FFD1]">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/60 text-base pb-5 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
            {t('cta.ready')}
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
            {t('cta.readyDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 text-lg font-medium h-auto">
                {t('nav.bookCall')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="bg-transparent text-white border border-white/20 hover:bg-white hover:text-black px-8 py-4 text-lg font-medium h-auto">
                {t('cta.exploreServices')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
